import React, { useEffect, useState, useCallback } from "react";
import { Share2, TrendingUp, RefreshCw } from "lucide-react";
import { api } from "../lib/api";

/**
 * Owner-only Referral Activity panel.
 * Aggregates the Share-My-Concierge funnel for the current ISO week.
 * Server-side endpoint enforces `company_settings` permission.
 * No PII — only counts on the experience_shares collection.
 */
export default function ReferralActivityPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setErr("");
    api.get("/admin/share-stats")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load referral activity."))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div className="p-6 text-center text-[#071A2D]/60">Loading referral activity…</div>;
  }
  if (err) {
    return (
      <div data-testid="referral-activity-error" className="p-4 rounded-2xl bg-[#FF7A45]/10 border border-[#FF7A45]/30 text-[#FF7A45] text-sm">
        {err}
      </div>
    );
  }
  if (!data) return null;

  const w = data.weekly || {};
  const a = data.all_time || {};

  return (
    <div data-testid="referral-activity-panel" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#064E3B] font-semibold flex items-center gap-1.5">
            <Share2 size={12}/> Referral Activity
          </div>
          <div className="text-sm text-[#071A2D]/70 mt-0.5">
            Share-My-Concierge links generated by customers and their downstream conversions. Week {data.week_key}.
          </div>
        </div>
        <button
          data-testid="referral-activity-refresh"
          onClick={load}
          className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#071A2D]/10 hover:bg-[#FFF8EE] flex items-center gap-1"
        >
          <RefreshCw size={12}/> Refresh
        </button>
      </div>

      <div className="p-5 rounded-2xl bg-[#071A2D] text-[#FFF8EE] border border-[#C9A45C]/30">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A45C] font-semibold">This Week</div>
        <div className="flex items-end gap-3 mt-2">
          <div data-testid="referral-shares-created" className="font-serif text-5xl leading-none">
            {w.shares_created ?? 0}
          </div>
          <div className="pb-1 text-sm opacity-80">Concierge shares</div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Stat label="Share Page Visits"      value={w.share_page_visits ?? 0}        testid="referral-visits"/>
        <Stat label="Signups From Shares"    value={w.new_signups_from_shares ?? 0}  testid="referral-signups"/>
        <Stat label="Bookings From Shares"   value={w.bookings_from_shares ?? 0}     testid="referral-bookings"/>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <Stat
          label="Signup Rate"
          value={`${(w.signup_rate_percent ?? 0).toFixed(1)}%`}
          testid="referral-signup-rate"
          icon={TrendingUp}
          accent
        />
        <Stat
          label="Booking Rate"
          value={`${(w.booking_rate_percent ?? 0).toFixed(1)}%`}
          testid="referral-booking-rate"
          icon={TrendingUp}
          accent
        />
      </div>

      <div className="text-[11px] text-[#071A2D]/55">
        All-time: <strong data-testid="referral-alltime-shares">{a.shares_created ?? 0}</strong> shares ·{" "}
        <strong data-testid="referral-alltime-visits">{a.share_page_visits ?? 0}</strong> visits ·{" "}
        <strong data-testid="referral-alltime-signups">{a.new_signups_from_shares ?? 0}</strong> signups ·{" "}
        <strong data-testid="referral-alltime-bookings">{a.bookings_from_shares ?? 0}</strong> bookings. No PII collected.
      </div>
    </div>
  );
}

const Stat = ({ label, value, testid, accent, icon: Icon }) => (
  <div
    data-testid={testid}
    className={`p-4 rounded-2xl border ${
      accent
        ? "bg-[#C9A45C]/12 border-[#C9A45C]/30"
        : "bg-[#FFF8EE] border-[#071A2D]/10"
    }`}
  >
    <div className="text-[10px] uppercase tracking-widest text-[#064E3B] font-semibold flex items-center gap-1.5">
      {Icon && <Icon size={11}/>} {label}
    </div>
    <div className="text-2xl font-serif mt-1 text-[#071A2D]">{value}</div>
  </div>
);
